import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import api from "@/lib/api";
import { formatINR } from "@/lib/utils";
import { TrendUp, TrendDown, Sparkle, Check, X, CircleNotch } from "@phosphor-icons/react";

/**
 * Public one-tap page for a Smart Pricing Alert (reached from the WhatsApp button
 * or a shared link). Possession of the token is the auth — no login required.
 */
export default function PricingAlert() {
  const { token } = useParams();
  const [state, setState] = useState("loading"); // loading | ready | applied | dismissed | error
  const [nudge, setNudge] = useState(null);
  const [err, setErr]   = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    let alive = true;
    api.get(`/pricing/nudge/${token}`)
      .then((r) => { if (alive) { setNudge(r.data); setState(r.data.status === "new" ? "ready" : r.data.status); } })
      .catch((e) => { if (alive) { setErr(e.response?.data?.detail || "This alert link is invalid or expired."); setState("error"); } });
    return () => { alive = false; };
  }, [token]);

  const act = async (kind) => {
    setBusy(true);
    try {
      const { data } = await api.post(`/pricing/nudge/${token}/${kind}`);
      if (kind === "apply") { setResult(data); setState("applied"); }
      else setState("dismissed");
    } catch (e) {
      setErr(e.response?.data?.detail || "Something went wrong.");
      setState("error");
    } finally {
      setBusy(false);
    }
  };

  const up = nudge?.direction === "up";
  const nights = nudge?.dates?.length || 0;

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-950 flex items-center justify-center p-4 font-outfit">
      <div className="w-full max-w-md bg-white dark:bg-slate-900 rounded-3xl shadow-xl border border-slate-100 dark:border-slate-800 p-6">
        <div className="flex items-center gap-2 mb-5">
          <span className="font-manrope text-xl font-bold tracking-tight">
            <span className="text-slate-800 dark:text-white">Host</span><span className="text-amber-500">Folio</span>
          </span>
          <span className="ml-auto inline-flex items-center gap-1 text-[11px] font-semibold text-amber-600 dark:text-amber-400">
            <Sparkle size={13} weight="fill" /> Smart Pricing
          </span>
        </div>

        {state === "loading" && (
          <div className="py-12 flex flex-col items-center text-slate-400">
            <CircleNotch size={28} className="animate-spin mb-2" /> Loading your pricing alert…
          </div>
        )}

        {state === "error" && (
          <div className="py-10 text-center">
            <X size={32} className="mx-auto mb-2 text-slate-300" />
            <p className="text-sm text-slate-500 dark:text-slate-400">{err}</p>
          </div>
        )}

        {state === "ready" && nudge && (
          <>
            <div className="flex items-center gap-2 flex-wrap mb-2">
              <span className={`inline-flex items-center gap-1 text-xs font-bold px-2 py-0.5 rounded-full ${up ? "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300" : "bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300"}`}>
                {up ? <TrendUp size={12} weight="bold" /> : <TrendDown size={12} weight="bold" />}
                {nudge.delta_pct > 0 ? `+${nudge.delta_pct}` : nudge.delta_pct}%
              </span>
              <span className="text-xs text-slate-400">{nudge.property_name}</span>
            </div>
            <h1 className="font-manrope text-lg font-bold text-slate-800 dark:text-white">{nudge.title}</h1>
            <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">{nudge.message}</p>

            <div className="mt-4 rounded-2xl bg-slate-50 dark:bg-slate-800 border border-slate-100 dark:border-slate-700 p-4 flex items-center justify-between">
              <div>
                <p className="text-[11px] text-slate-400 uppercase tracking-wide">Current</p>
                <p className="font-mono font-semibold text-slate-600 dark:text-slate-300">{formatINR(nudge.current_rate)}</p>
              </div>
              <span className="text-slate-300 text-xl">→</span>
              <div className="text-right">
                <p className="text-[11px] text-amber-500 uppercase tracking-wide">New rate</p>
                <p className="font-mono font-bold text-amber-600 dark:text-amber-400 text-lg">{formatINR(nudge.recommended_rate)}</p>
              </div>
            </div>
            <p className="text-[11px] text-slate-400 mt-2 text-center">
              Applies to {nights} night{nights === 1 ? "" : "s"}
              {nudge.dp_managed ? " · will be sent to your channel manager" : " · pushed to your OTAs"}
            </p>

            <div className="mt-5 flex gap-2">
              <button onClick={() => act("apply")} disabled={busy}
                className="flex-1 flex items-center justify-center gap-1.5 text-sm font-semibold bg-amber-400 hover:bg-amber-500 text-slate-900 rounded-xl px-4 py-3 transition disabled:opacity-50">
                <Check size={16} weight="bold" /> {busy ? "Applying…" : "Apply this price"}
              </button>
              <button onClick={() => act("dismiss")} disabled={busy}
                className="text-sm font-medium text-slate-400 hover:text-red-500 rounded-xl px-4 py-3 transition disabled:opacity-50">
                Dismiss
              </button>
            </div>
          </>
        )}

        {state === "applied" && (
          <div className="py-8 text-center">
            <div className="w-12 h-12 rounded-full bg-emerald-100 dark:bg-emerald-900/40 flex items-center justify-center mx-auto mb-3">
              <Check size={24} weight="bold" className="text-emerald-600 dark:text-emerald-400" />
            </div>
            <h2 className="font-manrope text-lg font-bold text-slate-800 dark:text-white">Price applied</h2>
            <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
              {result?.routed
                ? "Sent to your channel manager to update the OTAs."
                : result?.pushed
                  ? "Updated and pushed to your OTAs."
                  : "Your rates have been updated."}
            </p>
          </div>
        )}

        {state === "dismissed" && (
          <div className="py-8 text-center">
            <X size={28} className="mx-auto mb-2 text-slate-300" />
            <p className="text-sm text-slate-500 dark:text-slate-400">Alert dismissed. No changes were made.</p>
          </div>
        )}
      </div>
    </div>
  );
}
